import { useEffect, useState } from 'react';
import { PAYMENT_MODE_SOFT_GUARD_FIELDS } from '@easyflow/shared';
import type { InstallmentRepayment, PaymentModeAllowedEntity, RetailBrandPaymentMode, RetailBrandPaymentModeCreateInput, RetailBrandPaymentModeUpdateInput, RetailBrandPaymentModeUsage, RetailBrandPaymentModeWriteResponse, PaymentModeWriteImpact } from '@easyflow/shared';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-master/components/ui/alert-dialog';
import { Alert, AlertDescription } from '@easyflow/ui-master/components/ui/alert';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Label } from '@easyflow/ui-master/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-master/components/ui/select';
import { SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { Switch } from '@easyflow/ui-master/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@easyflow/ui-master/components/ui/table';
import { CreditCard, Pencil, Plus, Power, RefreshCw, Save, Trash2, X } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { ADE_PAYMENT_CODE_LABELS, ADE_PAYMENT_CODE_VALUES, INSTALLMENT_REPAYMENTS, PAYMENT_MODE_ACCOUNTING_EXPECTATIONS, PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABELS, PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABEL, PAYMENT_MODE_ADMIN_EXPECTATION_LABEL, PAYMENT_MODE_ALLOWED_DEBITS_LABEL, PAYMENT_MODE_ADMIN_EXPECTATIONS, PAYMENT_MODE_ADMIN_EXPECTATION_LABELS, PAYMENT_MODE_CREDIT_HOLDERS, PAYMENT_MODE_CREDIT_HOLDER_LABELS, PAYMENT_MODE_CREDIT_HOLDER_SHORT_LABELS, PAYMENT_MODE_FINANCED_FIELD_LABEL, PAYMENT_MODE_CODES_FIELD_LABEL, PAYMENT_MODE_AVAILABLE_PRODUCTS_LABEL, ALL_TENANT_PRODUCTS, TENANT_PRODUCT_LABELS, RetailBrandPaymentModeCreateSchema, installmentRepaymentLabel, toErr } from '@easyflow/shared';
import type { TenantProduct } from '@easyflow/shared';
import { Checkbox } from '@easyflow/ui-master/components/ui/checkbox';

interface PaymentModesSectionProps {
  brandId: string;
  modes: RetailBrandPaymentMode[];
  canManage: boolean;
  isLoading: boolean;
  loadError: string | null;
  onReload: () => Promise<void>;
}

const endpoint = '/master/retail-brand-payment-modes';
const notApplicable = '__not_applicable__';

/** Task #4333 — modalità referenziata da almeno una composizione/tariffa. */
const isInUse = (usage: RetailBrandPaymentModeUsage | null): boolean => usage != null && usage.compositions > 0;

const LOCKED_REASON = 'Modalità già usata nei listini: campo in sola lettura. Disattivala e creane una nuova.';
const WIDEN_ONLY_REASON = 'Modalità già usata nei listini: addebiti ed enti ammessi possono solo essere aggiunti, non tolti.';
const entitiesEndpoint = `${endpoint}/financing-entities`;
const USAGE_UNAVAILABLE = 'Impossibile verificare i listini che usano questa modalità: la modifica di codici RT/MP o la disattivazione richiedono il conteggio d\'impatto. Riprova.';

/** Utilizzo caricato, legato alla modalità a cui si riferisce (mai riusato per un'altra). */
interface LoadedUsage { modeId: string; data: RetailBrandPaymentModeUsage }

async function fetchUsage(modeId: string): Promise<RetailBrandPaymentModeUsage> {
  const response = await apiClient.get<RetailBrandPaymentModeUsage>(`${endpoint}/${modeId}/usage`);
  if (!response.success || !response.data) throw new Error(response.error ?? response.message ?? USAGE_UNAVAILABLE);
  return response.data;
}

/** Messaggi di conferma per una scrittura "morbida" (codici RT/MP, disattivazione) su modalità in uso; vuoto = nessuna conferma. */
export function softGuardMessages(
  current: RetailBrandPaymentMode,
  next: Pick<RetailBrandPaymentModeUpdateInput, 'ade_code' | 'mp_code' | 'is_active'>,
  usage: RetailBrandPaymentModeUsage,
): string[] {
  if (!isInUse(usage)) return [];
  const messages: string[] = [];
  const codesChanged = PAYMENT_MODE_SOFT_GUARD_FIELDS.some((field) => field in next && (next[field] ?? null) !== (current[field] ?? null));
  if (codesChanged) messages.push(`Stai cambiando i codici RT/MP di una modalità in uso: ${usage.tenants_inheriting_codes} tenant ereditano questi codici per default (${usageSummary(usage)}).`);
  if (current.is_active && next.is_active === false) messages.push(`Stai disattivando una modalità in uso: ${usage.active_compositions} composizioni attive resteranno agganciate finché i listini non cambiano modalità.`);
  return messages;
}

/** True se la scrittura tocca campi a guardia morbida (serve il conteggio d'impatto PRIMA della PATCH). */
const isImpactfulWrite = (current: RetailBrandPaymentMode, next: Pick<RetailBrandPaymentModeUpdateInput, 'ade_code' | 'mp_code' | 'is_active'>): boolean =>
  PAYMENT_MODE_SOFT_GUARD_FIELDS.some((field) => field in next && (next[field] ?? null) !== (current[field] ?? null))
  || (current.is_active && next.is_active === false);

function usageSummary(usage: RetailBrandPaymentModeUsage): string {
  return `${usage.compositions} composizioni (${usage.active_compositions} attive) su ${usage.price_lists} listini di ${usage.tenants} tenant`;
}

export function impactMessage(impact: PaymentModeWriteImpact): string {
  return impact.kind === 'deactivated'
    ? `Modalità disattivata: restano agganciate ${impact.usage.active_compositions} composizioni attive (${impact.usage.tenants} tenant). Gli editor listini le segnalano come "modalità disattivata" finché non cambiano modalità.`
    : `Codici RT/MP aggiornati: ${impact.usage.tenants_inheriting_codes} tenant ereditano i nuovi codici per default.`;
}

function newDraft(brandId: string): RetailBrandPaymentModeCreateInput {
  return {
    brand_id: brandId,
    name: '',
    code: '',
    credit_holder: 'brand',
    allowed_debits: [...INSTALLMENT_REPAYMENTS],
    allowed_entity_ids: [],
    accounting_expectation: 'brand_statement_credit',
    admin_expectation: 'none',
    ade_code: null,
    mp_code: null,
    available_products: [...ALL_TENANT_PRODUCTS],
    is_active: true,
    sort_order: 0,
  };
}

export default function PaymentModesSection({
  brandId,
  modes,
  canManage,
  isLoading,
  loadError,
  onReload,
}: PaymentModesSectionProps) {
  const { toast } = useToast();
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RetailBrandPaymentModeCreateInput>(() => newDraft(brandId));
  const [busyId, setBusyId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  // Task #4333 — utilizzo della modalità in modifica (campi bloccati + conferma impatto).
  const [loadedUsage, setLoadedUsage] = useState<LoadedUsage | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<{ messages: string[]; run: () => Promise<void> } | null>(null);
  const editingMode = editingId === null ? null : modes.find((mode) => mode.id === editingId) ?? null;
  const usage = loadedUsage !== null && loadedUsage.modeId === editingId ? loadedUsage.data : null;
  const locked = isInUse(usage);

  /** Utilizzo per la modalità indicata: riusa quello già caricato, altrimenti lo chiede al BE (fail-closed sul chiamante). */
  const ensureUsage = async (modeId: string): Promise<RetailBrandPaymentModeUsage> => {
    if (loadedUsage !== null && loadedUsage.modeId === modeId) return loadedUsage.data;
    const data = await fetchUsage(modeId);
    setLoadedUsage({ modeId, data });
    return data;
  };

  /**
   * Scrittura con guardia morbida: su campi d'impatto (codici RT/MP, disattivazione) il
   * conteggio d'impatto viene caricato PRIMA della PATCH e, se la modalità è in uso,
   * la scrittura parte solo dopo conferma. Usage non disponibile = nessuna scrittura.
   */
  const guardedWrite = async (current: RetailBrandPaymentMode, next: RetailBrandPaymentModeUpdateInput, run: () => Promise<void>): Promise<void> => {
    if (!isImpactfulWrite(current, next)) { await run(); return; }
    let currentUsage: RetailBrandPaymentModeUsage;
    try {
      currentUsage = await ensureUsage(current.id);
    } catch (err) {
      setErrors([toErr(err).message || USAGE_UNAVAILABLE]);
      return;
    }
    const messages = softGuardMessages(current, next, currentUsage);
    if (messages.length === 0) { await run(); return; }
    setPendingConfirm({ messages, run });
  };
  const formId = `payment-mode-${brandId}`;
  // Enti finanzianti globali (preset senza tenant): caricati all'apertura del form con ente terzo.
  const [entities, setEntities] = useState<PaymentModeAllowedEntity[] | null>(null);
  const [entitiesError, setEntitiesError] = useState<string | null>(null);
  const needsEntities = formOpen && draft.credit_holder === 'third_party';
  useEffect(() => {
    if (!needsEntities || entities !== null) return;
    let cancelled = false;
    apiClient.get<{ entities: PaymentModeAllowedEntity[] }>(entitiesEndpoint)
      .then((response) => {
        if (cancelled) return;
        if (!response.success || !response.data) throw new Error(response.error ?? response.message ?? 'Enti finanzianti non disponibili');
        setEntities(response.data.entities);
        setEntitiesError(null);
      })
      .catch((err: unknown) => { if (!cancelled) setEntitiesError(err instanceof Error ? err.message : 'Enti finanzianti non disponibili'); });
    return () => { cancelled = true; };
  }, [needsEntities, entities]);
  // In uso: addebiti/enti già ammessi non si tolgono (orfanerebbero composizioni salvate).
  const lockedDebits = new Set<InstallmentRepayment>(locked && editingMode ? editingMode.allowed_debits : []);
  const lockedEntityIds = new Set<string>(locked && editingMode ? editingMode.allowed_entities.map((entity) => entity.id) : []);
  const toggleDebit = (debit: InstallmentRepayment, checked: boolean): void => {
    setDraft((current) => ({
      ...current,
      allowed_debits: checked
        ? [...new Set([...current.allowed_debits, debit])]
        : current.allowed_debits.filter((entry) => entry !== debit),
    }));
  };
  const toggleEntity = (entityId: string, checked: boolean): void => {
    setDraft((current) => {
      const ids = current.allowed_entity_ids ?? [];
      return { ...current, allowed_entity_ids: checked ? [...new Set([...ids, entityId])] : ids.filter((entry) => entry !== entityId) };
    });
  };

  const closeForm = (): void => {
    setFormOpen(false);
    setEditingId(null);
    setErrors([]);
    setLoadedUsage(null);
  };

  const startCreate = (): void => {
    setEditingId(null);
    setDraft(newDraft(brandId));
    setErrors([]);
    setFormOpen(true);
  };

  const startEdit = (mode: RetailBrandPaymentMode): void => {
    setEditingId(mode.id);
    setDraft({
      brand_id: mode.brand_id,
      name: mode.name,
      code: mode.code,
      credit_holder: mode.credit_holder,
      allowed_debits: [...mode.allowed_debits],
      allowed_entity_ids: mode.allowed_entities.map((entity) => entity.id),
      accounting_expectation: mode.accounting_expectation,
      admin_expectation: mode.admin_expectation,
      ade_code: mode.ade_code,
      mp_code: mode.mp_code,
      available_products: [...mode.available_products],
      is_active: mode.is_active,
      sort_order: mode.sort_order,
    });
    setErrors([]);
    setLoadedUsage(null);
    setFormOpen(true);
    // Pre-carica l'utilizzo per mostrare i campi bloccati; se fallisce, le scritture d'impatto lo richiedono al salvataggio.
    void fetchUsage(mode.id).then((data) => setLoadedUsage({ modeId: mode.id, data })).catch(() => undefined);
  };

  const save = async (): Promise<void> => {
    if (!canManage || busyId !== null) return;
    // Anche le modifiche sono validate come forma completa: la PATCH sul BE
    // fonde il payload con la riga corrente e applica le stesse invarianti.
    const parsed = RetailBrandPaymentModeCreateSchema.safeParse({
      ...draft,
      brand_id: brandId,
      name: draft.name.trim(),
      code: draft.code?.trim() || undefined,
      mp_code: draft.mp_code?.trim().toUpperCase() || null,
    });
    if (!parsed.success) {
      setErrors(parsed.error.issues.map((issue) => issue.message));
      return;
    }
    setErrors([]);
    const run = async (): Promise<void> => {
      setBusyId(editingId ?? '__new__');
      try {
        const response = editingId === null
          ? await apiClient.post<RetailBrandPaymentModeWriteResponse>(endpoint, parsed.data)
          : await apiClient.patch<RetailBrandPaymentModeWriteResponse>(
            `${endpoint}/${editingId}`,
            parsed.data satisfies RetailBrandPaymentModeUpdateInput,
          );
        if (!response.success) throw new Error(response.error ?? response.message ?? 'Salvataggio non riuscito');
        const impacts = response.data?.impacts ?? [];
        toast({
          title: editingId === null ? 'Modalità creata' : 'Modalità aggiornata',
          description: impacts.length > 0 ? impacts.map(impactMessage).join(' ') : parsed.data.name,
        });
        closeForm();
        await onReload();
      } catch (err) {
        setErrors([toErr(err).message]);
      } finally {
        setBusyId(null);
      }
    };
    // Task #4333 — guardia morbida: codici RT/MP e disattivazione su modalità in uso richiedono conferma con l'impatto.
    if (editingMode) { await guardedWrite(editingMode, parsed.data, run); return; }
    await run();
  };

  const toggleActive = async (mode: RetailBrandPaymentMode): Promise<void> => {
    if (!canManage || busyId !== null) return;
    setErrors([]);
    const payload: RetailBrandPaymentModeUpdateInput = { is_active: !mode.is_active };
    const run = async (): Promise<void> => {
      setBusyId(mode.id);
      try {
        const response = await apiClient.patch<RetailBrandPaymentModeWriteResponse>(`${endpoint}/${mode.id}`, payload);
        if (!response.success) throw new Error(response.error ?? response.message ?? 'Aggiornamento non riuscito');
        const impacts = response.data?.impacts ?? [];
        toast({ title: mode.is_active ? 'Modalità disattivata' : 'Modalità attivata', description: impacts.length > 0 ? impacts.map(impactMessage).join(' ') : mode.name });
        await onReload();
      } catch (err) {
        setErrors([toErr(err).message]);
      } finally {
        setBusyId(null);
      }
    };
    // Task #4333 — la disattivazione dalla tabella passa dalla stessa guardia del form.
    setBusyId(mode.id);
    try {
      await guardedWrite(mode, payload, run);
    } finally {
      setBusyId((current) => (current === mode.id ? null : current));
    }
  };

  const remove = async (mode: RetailBrandPaymentMode): Promise<void> => {
    if (!canManage || busyId !== null) return;
    if (!window.confirm(`Eliminare la modalità "${mode.name}"? L'operazione è definitiva. Se è in uso da listini, puoi disattivarla invece di eliminarla.`)) return;
    setBusyId(mode.id);
    setErrors([]);
    try {
      const response = await apiClient.delete(`${endpoint}/${mode.id}`);
      if (!response.success) throw new Error(response.error ?? response.message ?? 'Eliminazione non riuscita');
      if (editingId === mode.id) closeForm();
      toast({ title: 'Modalità eliminata', description: mode.name });
      await onReload();
    } catch (err) {
      // Include il messaggio IN_USE (409) e gli altri errori restituiti dal BE.
      setErrors([toErr(err).message]);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="space-y-4" aria-labelledby={`${formId}-title`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1">
          <h2 id={`${formId}-title`} className="flex items-center gap-2 text-base font-semibold">
            <CreditCard className="h-4 w-4 text-muted-foreground" />
            Modalità di pagamento dell'insegna
          </h2>
          <p className="text-xs text-muted-foreground">
            Template dell'insegna: ente che finanzia, addebiti ammessi e attese; selezionabili solo nelle composizioni dei listini bundle.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isLoading && loadError === null && (
            <Badge variant="outline">{canManage ? 'Gestione abilitata' : 'Sola lettura'}</Badge>
          )}
          {canManage && (
            <Button size="sm" variant="outline" onClick={startCreate} disabled={isLoading || busyId !== null || formOpen}>
              <Plus className="mr-2 h-4 w-4" />
              Nuova modalità
            </Button>
          )}
        </div>
      </div>

      {loadError !== null && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
            <span>{loadError}</span>
            <Button size="sm" variant="outline" onClick={() => void onReload()} disabled={isLoading}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Riprova
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>
            <ul className="list-inside list-disc space-y-1">
              {errors.map((error, index) => <li key={`${index}-${error}`}>{error}</li>)}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {formOpen && canManage && (
        <form
          className="space-y-4 rounded-lg border border-border bg-muted/40 p-4"
          onSubmit={(event) => { event.preventDefault(); void save(); }}
          noValidate
        >
          <h3 className="text-sm font-medium">{editingId === null ? 'Nuova modalità di pagamento' : 'Modifica modalità di pagamento'}</h3>
          {editingId !== null && (
            <p className="text-xs text-muted-foreground" data-testid="payment-mode-usage">
              {usage === null
                ? 'Verifica dei listini che usano la modalità in corso: codici RT/MP e disattivazione richiedono il conteggio d\'impatto prima del salvataggio.'
                : locked
                  ? `In uso: ${usageSummary(usage)}. Codice, titolare del credito, rimborso rate e attesa amministrativa sono in sola lettura; i codici RT/MP restano modificabili con conferma.`
                  : 'Nessun listino usa questa modalità: tutti i campi sono modificabili.'}
            </p>
          )}
          <fieldset disabled={busyId !== null || isLoading} className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor={`${formId}-name`} className="text-xs text-muted-foreground">Nome commerciale *</Label>
              <Input
                id={`${formId}-name`}
                autoFocus
                maxLength={128}
                value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${formId}-code`} className="text-xs text-muted-foreground">Codice (opzionale)</Label>
              <Input
                id={`${formId}-code`}
                placeholder="Automatico"
                maxLength={64}
                value={draft.code ?? ''}
                readOnly={locked}
                aria-readonly={locked}
                title={locked ? LOCKED_REASON : undefined}
                onChange={(event) => setDraft((current) => ({ ...current, code: event.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${formId}-holder`} className="text-xs text-muted-foreground">{PAYMENT_MODE_FINANCED_FIELD_LABEL} *</Label>
              <Select
                value={draft.credit_holder}
                disabled={locked}
                onValueChange={(value) => {
                  const holder = PAYMENT_MODE_CREDIT_HOLDERS.find((entry) => entry === value);
                  if (holder === undefined) return;
                  setDraft((current) => ({
                    ...current,
                    credit_holder: holder,
                    ...(holder === 'third_party'
                      ? { admin_expectation: 'none', accounting_expectation: 'entity_bank_transfer' }
                      : { allowed_entity_ids: [], accounting_expectation: 'brand_statement_credit' }),
                  }));
                }}
              >
                <SelectTrigger id={`${formId}-holder`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODE_CREDIT_HOLDERS.map((holder) => (
                    <SelectItem key={holder} value={holder}>
                      {PAYMENT_MODE_CREDIT_HOLDER_LABELS[holder]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1" data-testid="payment-mode-allowed-debits">
              <Label className="text-xs text-muted-foreground">{PAYMENT_MODE_ALLOWED_DEBITS_LABEL} *</Label>
              <div className="flex h-10 items-center gap-4" title={locked ? WIDEN_ONLY_REASON : undefined}>
                {INSTALLMENT_REPAYMENTS.map((debit) => (
                  <label key={debit} htmlFor={`${formId}-debit-${debit}`} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      id={`${formId}-debit-${debit}`}
                      checked={draft.allowed_debits.includes(debit)}
                      disabled={lockedDebits.has(debit)}
                      onCheckedChange={(checked) => toggleDebit(debit, checked === true)}
                    />
                    {installmentRepaymentLabel(debit)}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${formId}-accounting`} className="text-xs text-muted-foreground">{PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABEL} *</Label>
              <Select
                value={draft.accounting_expectation}
                disabled={locked}
                onValueChange={(value) => {
                  const expectation = PAYMENT_MODE_ACCOUNTING_EXPECTATIONS.find((entry) => entry === value);
                  if (expectation !== undefined) setDraft((current) => ({ ...current, accounting_expectation: expectation }));
                }}
              >
                <SelectTrigger id={`${formId}-accounting`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODE_ACCOUNTING_EXPECTATIONS.map((expectation) => (
                    <SelectItem key={expectation} value={expectation}>{PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABELS[expectation]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {draft.credit_holder === 'third_party' && (
              <div className="space-y-1 md:col-span-2" data-testid="payment-mode-allowed-entities">
                <Label className="text-xs text-muted-foreground">Enti finanzianti ammessi *</Label>
                {entitiesError !== null ? (
                  <Alert variant="destructive"><AlertDescription>{entitiesError}</AlertDescription></Alert>
                ) : entities === null ? (
                  <p className="text-xs text-muted-foreground">Caricamento enti…</p>
                ) : entities.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nessun ente finanziante globale attivo: crearlo prima in Anagrafiche.</p>
                ) : (
                  <div className="flex flex-wrap gap-x-4 gap-y-2" title={locked ? WIDEN_ONLY_REASON : undefined}>
                    {entities.map((entity) => (
                      <label key={entity.id} htmlFor={`${formId}-entity-${entity.id}`} className="flex cursor-pointer items-center gap-2 text-sm">
                        <Checkbox
                          id={`${formId}-entity-${entity.id}`}
                          checked={(draft.allowed_entity_ids ?? []).includes(entity.id)}
                          disabled={lockedEntityIds.has(entity.id)}
                          onCheckedChange={(checked) => toggleEntity(entity.id, checked === true)}
                        />
                        {entity.trade_name ?? entity.legal_name}{entity.vat_number ? <span className="font-mono text-xs text-muted-foreground">{entity.vat_number}</span> : null}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-1">
              <Label htmlFor={`${formId}-expectation`} className="text-xs text-muted-foreground">{PAYMENT_MODE_ADMIN_EXPECTATION_LABEL} *</Label>
              <Select
                value={draft.admin_expectation}
                disabled={locked || draft.credit_holder !== 'brand'}
                onValueChange={(value) => {
                  const expectation = PAYMENT_MODE_ADMIN_EXPECTATIONS.find((entry) => entry === value);
                  if (expectation !== undefined) setDraft((current) => ({ ...current, admin_expectation: expectation }));
                }}
              >
                <SelectTrigger id={`${formId}-expectation`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODE_ADMIN_EXPECTATIONS.map((expectation) => (
                    <SelectItem key={expectation} value={expectation}>{PAYMENT_MODE_ADMIN_EXPECTATION_LABELS[expectation]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${formId}-ade`} className="text-xs text-muted-foreground">Codice RT quota finanziata (default)</Label>
              <Select
                value={draft.ade_code == null ? notApplicable : String(draft.ade_code)}
                onValueChange={(value) => {
                  const code = ADE_PAYMENT_CODE_VALUES.find((entry) => String(entry) === value);
                  setDraft((current) => ({ ...current, ade_code: code ?? null }));
                }}
              >
                <SelectTrigger id={`${formId}-ade`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={notApplicable}>Non applicabile</SelectItem>
                  {ADE_PAYMENT_CODE_VALUES.map((code) => (
                    <SelectItem key={code} value={String(code)}>{code} — {ADE_PAYMENT_CODE_LABELS[code]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${formId}-mp`} className="text-xs text-muted-foreground">Codice fattura quota finanziata (MPnn, default)</Label>
              <Input
                id={`${formId}-mp`}
                placeholder="MPnn"
                maxLength={4}
                value={draft.mp_code ?? ''}
                onChange={(event) => setDraft((current) => ({ ...current, mp_code: event.target.value.toUpperCase() }))}
              />
            </div>
            <div className="space-y-1" data-testid="payment-mode-available-products">
              <Label className="text-xs text-muted-foreground">{PAYMENT_MODE_AVAILABLE_PRODUCTS_LABEL} *</Label>
              <div className="flex h-10 items-center gap-4">
                {ALL_TENANT_PRODUCTS.map((product: TenantProduct) => {
                  const checked = (draft.available_products ?? []).includes(product);
                  return (
                    <label key={product} htmlFor={`${formId}-product-${product}`} className="flex cursor-pointer items-center gap-2 text-sm">
                      <Checkbox
                        id={`${formId}-product-${product}`}
                        checked={checked}
                        onCheckedChange={(next) => setDraft((current) => {
                          const set = new Set(current.available_products ?? []);
                          if (next === true) set.add(product); else set.delete(product);
                          return { ...current, available_products: ALL_TENANT_PRODUCTS.filter((key) => set.has(key)) };
                        })}
                      />
                      {TENANT_PRODUCT_LABELS[product]}
                    </label>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`${formId}-sort`} className="text-xs text-muted-foreground">Ordinamento</Label>
              <Input
                id={`${formId}-sort`}
                type="number"
                step={1}
                value={Number.isFinite(draft.sort_order) ? draft.sort_order : ''}
                onChange={(event) => setDraft((current) => ({ ...current, sort_order: event.target.valueAsNumber }))}
              />
            </div>
            <div className="flex items-center gap-2 self-end pb-2">
              <Switch
                id={`${formId}-active`}
                checked={draft.is_active ?? true}
                onCheckedChange={(isActive) => setDraft((current) => ({ ...current, is_active: isActive }))}
              />
              <Label htmlFor={`${formId}-active`}>Attiva</Label>
            </div>
          </fieldset>
          <p className="text-xs text-muted-foreground">
            Con credito è obbligatorio almeno un codice RT o fattura. Il pagamento unico non prevede attesa, rimborso rate o codici della quota finanziata.
          </p>
          <div className="flex gap-2">
            <Button size="sm" type="submit" disabled={busyId !== null || isLoading}>
              <Save className="mr-2 h-4 w-4" />
              {editingId === null ? 'Crea' : 'Salva'}
            </Button>
            <Button size="sm" variant="ghost" type="button" onClick={closeForm} disabled={busyId !== null}>
              <X className="mr-2 h-4 w-4" />
              Annulla
            </Button>
          </div>
        </form>
      )}

      {isLoading ? <SkeletonTable rows={3} /> : loadError === null && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Modalità / codice</TableHead>
              <TableHead>{PAYMENT_MODE_FINANCED_FIELD_LABEL}</TableHead>
              <TableHead>{PAYMENT_MODE_ALLOWED_DEBITS_LABEL}</TableHead>
              <TableHead>Enti ammessi</TableHead>
              <TableHead>{PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABEL} / {PAYMENT_MODE_ADMIN_EXPECTATION_LABEL}</TableHead>
              <TableHead title={PAYMENT_MODE_CODES_FIELD_LABEL}>Codici quota finanziata (default)</TableHead>
              <TableHead>{PAYMENT_MODE_AVAILABLE_PRODUCTS_LABEL}</TableHead>
              <TableHead className="text-center">Ordine</TableHead>
              <TableHead className="text-center">Stato</TableHead>
              {canManage && <TableHead className="text-right">Azioni</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {modes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canManage ? 9 : 8} className="py-6 text-center text-sm text-muted-foreground">
                  Nessuna modalità di pagamento per questa insegna
                </TableCell>
              </TableRow>
            ) : modes.map((mode) => (
              <TableRow key={mode.id} className={mode.is_active ? '' : 'opacity-60'}>
                <TableCell>
                  <div className="font-medium">{mode.name}</div>
                  <div className="font-mono text-xs text-muted-foreground">{mode.code}</div>
                </TableCell>
                <TableCell><Badge variant="outline" title={PAYMENT_MODE_CREDIT_HOLDER_LABELS[mode.credit_holder]}>{PAYMENT_MODE_CREDIT_HOLDER_SHORT_LABELS[mode.credit_holder]}</Badge></TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {mode.allowed_debits.map((debit) => <Badge key={debit} variant="outline">{installmentRepaymentLabel(debit, 'short')}</Badge>)}
                  </div>
                </TableCell>
                <TableCell>
                  {mode.credit_holder === 'third_party' ? (
                    <div className="flex flex-wrap gap-1">
                      {mode.allowed_entities.map((entity) => <Badge key={entity.id} variant="secondary">{entity.trade_name ?? entity.legal_name}</Badge>)}
                    </div>
                  ) : <span className="text-xs text-muted-foreground">Insegna</span>}
                </TableCell>
                <TableCell>
                  <div className="flex flex-col gap-1 text-xs">
                    <span>{PAYMENT_MODE_ACCOUNTING_EXPECTATION_LABELS[mode.accounting_expectation]}</span>
                    <span className="text-muted-foreground">{PAYMENT_MODE_ADMIN_EXPECTATION_LABELS[mode.admin_expectation]}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {mode.ade_code !== null && <Badge variant="outline">RT {mode.ade_code} — {ADE_PAYMENT_CODE_LABELS[mode.ade_code]}</Badge>}
                    {mode.mp_code !== null && <Badge variant="outline">{mode.mp_code}</Badge>}
                    {mode.ade_code === null && mode.mp_code === null && <span className="text-xs text-muted-foreground">Non applicabile</span>}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {mode.available_products.map((product) => (
                      <Badge key={product} variant="secondary">{TENANT_PRODUCT_LABELS[product]}</Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-center">{mode.sort_order}</TableCell>
                <TableCell className="text-center"><Badge variant={mode.is_active ? 'default' : 'secondary'}>{mode.is_active ? 'attiva' : 'inattiva'}</Badge></TableCell>
                {canManage && (
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" title={mode.is_active ? 'Disattiva modalità' : 'Attiva modalità'} aria-label={`${mode.is_active ? 'Disattiva' : 'Attiva'} ${mode.name}`} disabled={busyId !== null || formOpen} onClick={() => void toggleActive(mode)}>
                        <Power className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Modifica modalità" aria-label={`Modifica ${mode.name}`} disabled={busyId !== null || formOpen} onClick={() => startEdit(mode)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" title="Elimina modalità" aria-label={`Elimina ${mode.name}`} disabled={busyId !== null || formOpen} onClick={() => void remove(mode)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <AlertDialog open={pendingConfirm !== null} onOpenChange={(open) => { if (!open) setPendingConfirm(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi la modifica di una modalità in uso?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                {pendingConfirm?.messages.map((message) => <p key={message}>{message}</p>)}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => { const pending = pendingConfirm; setPendingConfirm(null); if (pending) void pending.run(); }}>Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
